import React, { useCallback, useState } from 'react';
import './App.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { MobileInputForm } from './components/MobileInputForm';
import { MobileResultsView } from './components/MobileResultsView';
import { MobileNavigation } from './components/MobileNavigation';
import { ETTCalculationsSection } from './components/ETTCalculationsSection';
import { ReferencesSection } from './components/ReferencesSection';
import { useFormState } from './hooks/useFormState';
import { useAirwayCalculations } from './hooks/useAirwayCalculations';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
      },
    },
  },
});

function App() {
  const {
    ageValue,
    height,
    weight,
    heightUnit,
    weightUnit,
    heightInput,
    weightInput,
    hasSyndrome,
    setAgeValue,
    handleHeightInputChange,
    handleWeightInputChange,
    handleHeightUnitChange,
    handleWeightUnitChange,
    handleReset,
    toggleETTCalculations,
  } = useFormState();

  const [results, setResults] = useState<any>(null);
  const [currentView, setCurrentView] = useState<'devices' | 'calculations' | 'references'>('devices');
  const calculationResults = useAirwayCalculations(ageValue, height, weight, hasSyndrome);

  const handleCalculate = useCallback(() => {
    if (!ageValue) {
      alert('Please enter an age');
      return;
    }

    try {
      if (calculationResults) {
        setResults(calculationResults);
      }
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('An error occurred while calculating results');
      }
    }
  }, [ageValue, calculationResults]);

  const handleResetWithResults = useCallback(() => {
    handleReset();
    setResults(null);
    setCurrentView('devices');
  }, [handleReset]);

  const renderCurrentView = () => {
    if (!results && currentView !== 'references') {
      return (
        <MobileInputForm
          ageValue={ageValue}
          onAgeChange={setAgeValue}
          heightInput={heightInput}
          onHeightInputChange={handleHeightInputChange}
          heightUnit={heightUnit}
          onHeightUnitChange={handleHeightUnitChange}
          weightInput={weightInput}
          onWeightInputChange={handleWeightInputChange}
          weightUnit={weightUnit}
          onWeightUnitChange={handleWeightUnitChange}
          onCalculate={handleCalculate}
          onReset={handleResetWithResults}
        />
      );
    }

    switch (currentView) {
      case 'devices':
        return (
          <>
            {!results && (
              <MobileInputForm
                ageValue={ageValue}
                onAgeChange={setAgeValue}
                heightInput={heightInput}
                onHeightInputChange={handleHeightInputChange}
                heightUnit={heightUnit}
                onHeightUnitChange={handleHeightUnitChange}
                weightInput={weightInput}
                onWeightInputChange={handleWeightInputChange}
                weightUnit={weightUnit}
                onWeightUnitChange={handleWeightUnitChange}
                onCalculate={handleCalculate}
                onReset={handleResetWithResults}
              />
            )}
            {results && (
              <MobileResultsView
                results={results}
                weight={weight}
                ageValue={ageValue}
                heightInput={heightInput}
                weightInput={weightInput}
                heightUnit={heightUnit}
                weightUnit={weightUnit}
                onNavigateToCalculations={() => setCurrentView('calculations')}
              />
            )}
          </>
        );
      case 'calculations':
        return results ? (
          <Box sx={{ p: 2 }}>
            <ETTCalculationsSection
              ettCalculations={results.ettCalculations}
              measurements={results.measurements}
              showETTCalculations={true}
              onToggleETTCalculations={toggleETTCalculations}
              onNavigateToReferences={() => setCurrentView('references')}
              ageValue={ageValue}
              heightInput={heightInput}
              weightInput={weightInput}
              heightUnit={heightUnit}
              weightUnit={weightUnit}
              results={results}
            />
          </Box>
        ) : null;
      case 'references':
        return (
          <Box sx={{ p: 2 }}>
            <ReferencesSection />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <MobileNavigation
          currentView={currentView}
          onViewChange={setCurrentView}
          hasResults={!!results}
          onReset={handleResetWithResults}
        />
        
        <Box sx={{ 
          pb: 2,
          maxWidth: '800px',
          mx: 'auto',
          px: { xs: 0, sm: 2 }
        }}>
          {renderCurrentView()}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;